import { motion, AnimatePresence } from 'framer-motion'
import { useState, useMemo } from 'react'
import {
  format, startOfWeek, endOfWeek, eachDayOfInterval,
  addWeeks, subWeeks, isToday, isSameDay,
  getHours, getMinutes, isWithinInterval
} from 'date-fns'
import { ChevronLeft, ChevronRight, Clock, Users, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EventType } from './mockEventData'
import { cn } from '@/lib/utils'

interface CalendarWeekProps {
  events: EventType[]
  selectedDate?: Date
  onDateSelect?: (date: Date) => void
  onEventClick?: (event: EventType) => void
  className?: string
}

interface TimeSlot {
  hour: number
  events: EventType[]
}

export function CalendarWeek({
  events,
  selectedDate,
  onDateSelect,
  onEventClick,
  className
}: CalendarWeekProps) {
  const [currentWeek, setCurrentWeek] = useState(selectedDate || new Date())
  const [isAnimating, setIsAnimating] = useState(false)

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 0 })
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 0 })
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd })

  // Generate time slots (6 AM to 10 PM)
  const timeSlots = useMemo(() => {
    const slots: TimeSlot[] = []
    for (let hour = 6; hour <= 22; hour++) {
      slots.push({ hour, events: [] })
    }
    return slots
  }, [])

  // Group events by day and time
  const eventsByDay = useMemo(() => {
    const grouped: Record<string, EventType[]> = {}

    weekDays.forEach(day => {
      const dayKey = format(day, 'yyyy-MM-dd')
      grouped[dayKey] = events.filter(event => {
        const eventDate = format(event.startTime, 'yyyy-MM-dd')
        return eventDate === dayKey
      }).sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
    })

    return grouped
  }, [events, weekDays])

  const navigateWeek = (direction: 'prev' | 'next') => {
    if (isAnimating) return

    setIsAnimating(true)
    setCurrentWeek(prev => direction === 'next' ? addWeeks(prev, 1) : subWeeks(prev, 1))

    setTimeout(() => setIsAnimating(false), 300)
  }

  const getEventPosition = (event: EventType) => {
    const startHour = getHours(event.startTime)
    const startMinutes = getMinutes(event.startTime)
    const endHour = getHours(event.endTime)
    const endMinutes = getMinutes(event.endTime)

    const startPercent = ((startHour - 6) * 60 + startMinutes) / (17 * 60) * 100
    const duration = (endHour * 60 + endMinutes) - (startHour * 60 + startMinutes)
    const heightPercent = (duration / (17 * 60)) * 100

    return {
      top: `${Math.max(0, startPercent)}%`,
      height: `${Math.min(heightPercent, 100 - startPercent)}%`
    }
  }

  const getEventTypeColor = (type: EventType['type']) => {
    const colors = {
      demo: 'from-blue-500 to-cyan-500',
      webinar: 'from-purple-500 to-pink-500',
      workshop: 'from-orange-500 to-red-500',
      tour: 'from-green-500 to-teal-500',
      launch: 'from-yellow-400 to-orange-500'
    }
    return colors[type] || 'from-gray-500 to-gray-600'
  }

  return (
    <div className={cn("w-full max-w-7xl mx-auto", className)}>
      {/* Week Header */}
      <div className="flex items-center justify-between mb-8">
        <motion.h2
          key={format(weekStart, 'MMM dd') + '-' + format(weekEnd, 'dd, yyyy')}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-3xl font-bold text-gray-900 dark:text-white"
        >
          {format(weekStart, 'MMM dd')} - {format(weekEnd, 'dd, yyyy')}
        </motion.h2>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateWeek('prev')}
            disabled={isAnimating}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentWeek(new Date())}
          >
            This Week
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateWeek('next')}
            disabled={isAnimating}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Week View */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Days Header */}
        <div className="grid grid-cols-8 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <div className="p-4">
            <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">Time</span>
          </div>
          {weekDays.map((day, index) => (
            <motion.div
              key={day.toISOString()}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={cn(
                "p-4 text-center cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors",
                isToday(day) && "bg-blue-100 dark:bg-blue-900/30",
                selectedDate && isSameDay(day, selectedDate) && "bg-blue-200 dark:bg-blue-900/40"
              )}
              onClick={() => {
                onDateSelect?.(day)
                setCurrentWeek(day)
              }}
            >
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {format(day, 'EEE')}
              </div>
              <div className={cn(
                "text-lg font-bold mt-1",
                isToday(day) ? "text-blue-600 dark:text-blue-400" : "text-gray-900 dark:text-white"
              )}>
                {format(day, 'd')}
              </div>
              {eventsByDay[format(day, 'yyyy-MM-dd')]?.length > 0 && (
                <div className="w-2 h-2 bg-blue-500 rounded-full mx-auto mt-1" />
              )}
            </motion.div>
          ))}
        </div>

        {/* Time Grid */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={format(weekStart, 'yyyy-MM-dd')}
              initial={{ opacity: 0, x: isAnimating ? 100 : 0 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: isAnimating ? -100 : 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-8"
            >
              {/* Time labels column */}
              <div className="border-r border-gray-200 dark:border-gray-700">
                {timeSlots.map(({ hour }) => (
                  <div key={hour} className="h-16 p-2 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                    {format(new Date().setHours(hour, 0, 0, 0), 'HH:mm')}
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {weekDays.map((day, dayIndex) => {
                const dayKey = format(day, 'yyyy-MM-dd')
                const dayEvents = eventsByDay[dayKey] || []

                return (
                  <div key={day.toISOString()} className="relative border-r border-gray-100 dark:border-gray-700 last:border-r-0">
                    {/* Time slot backgrounds */}
                    {timeSlots.map(({ hour }) => (
                      <div
                        key={hour}
                        className={cn(
                          "h-16 border-b border-gray-100 dark:border-gray-700 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-colors cursor-pointer",
                          hour % 2 === 0 ? "bg-gray-50/30 dark:bg-gray-800/30" : "bg-white dark:bg-gray-800"
                        )}
                        onClick={() => onDateSelect?.(day)}
                      />
                    ))}

                    {/* Events */}
                    {dayEvents.map((event, eventIndex) => {
                      const position = getEventPosition(event)
                      const isLongEvent = (event.endTime.getTime() - event.startTime.getTime()) > 2 * 60 * 60 * 1000 // 2+ hours

                      return (
                        <motion.div
                          key={event.id}
                          initial={{ opacity: 0, scale: 0.8, y: 10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          transition={{ delay: dayIndex * 0.05 + eventIndex * 0.1 }}
                          whileHover={{ scale: 1.02, zIndex: 10 }}
                          className="absolute left-1 right-1 z-10 cursor-pointer group"
                          style={position}
                          onClick={() => onEventClick?.(event)}
                        >
                          <div className={cn(
                            "h-full rounded-lg p-2 shadow-sm border border-white/20 dark:border-black/20 bg-gradient-to-r text-white overflow-hidden group-hover:shadow-lg transition-shadow",
                            getEventTypeColor(event.type)
                          )}>
                            <div className="text-xs font-semibold truncate">
                              {event.title}
                            </div>

                            {isLongEvent && (
                              <>
                                <div className="text-xs opacity-90 flex items-center gap-1 mt-1">
                                  <Clock className="w-3 h-3" />
                                  {format(event.startTime, 'HH:mm')} - {format(event.endTime, 'HH:mm')}
                                </div>

                                <div className="text-xs opacity-80 flex items-center gap-1 mt-1">
                                  <MapPin className="w-3 h-3" />
                                  {event.location}
                                </div>

                                <div className="text-xs opacity-80 flex items-center gap-1 mt-1">
                                  <Users className="w-3 h-3" />
                                  {event.currentAttendees}/{event.maxAttendees}
                                </div>
                              </>
                            )}

                            {!isLongEvent && (
                              <div className="text-xs opacity-90 mt-0.5">
                                {format(event.startTime, 'HH:mm')}
                              </div>
                            )}

                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg" />
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>
                )
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Current time indicator */}
      <div className="relative">
        {isToday(weekStart) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute left-0 right-0 pointer-events-none"
            style={{
              top: `${((getHours(new Date()) - 6) * 64 + (getMinutes(new Date()) / 60 * 64))}px`
            }}
          >
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full border-2 border-white shadow-lg" />
              <div className="flex-1 h-0.5 bg-red-500" />
            </div>
          </motion.div>
        )}
      </div>

      {/* Week Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        {[
          { label: 'Total Events', value: Object.values(eventsByDay).flat().length },
          { label: 'Today', value: eventsByDay[format(new Date(), 'yyyy-MM-dd')]?.length || 0 },
          { label: 'Upcoming', value: Object.values(eventsByDay).flat().filter(e => e.startTime > new Date()).length },
          { label: 'This Week', value: events.filter(e =>
            isWithinInterval(e.startTime, { start: weekStart, end: weekEnd })
          ).length }
        ].map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stat.value}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {stat.label}
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  )
}